import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import React, { useCallback, useEffect, useState } from 'react';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { Hero } from '../components/Hero';
import { SearchBar } from '../components/SearchBar';
import { JobList } from '../components/JobList';
import { CategoryListWithHighlights } from '../components/CategoryListWithHighlights';
import { Cta } from '../components/Cta';
import { Banner } from '../components/Banner';
import { useSearch } from '../hooks/useSearch';
import { useTracking } from '../hooks/useTracking';
import { useT } from '../i18n/useT';
import { readUrlParams, pushUrlParams, toBackendParams, applyLocationMode } from '../hooks/useSearchUrlSync';
function PageHeading({ count }) {
    const { t } = useT();
    const subtitleTpl = t('index_active_jobs_subtitle', '');
    const subtitleStr = subtitleTpl
        ? subtitleTpl.replace('{count}', String(count))
        : `${count} aktív állás vár rád`;
    return (_jsx("section", { className: "career-page-heading", children: _jsxs("div", { className: "container mx-auto px-4", children: [_jsx("h1", { "data-translate": "index_page_title", children: t('index_page_title', 'Álláslehetőségek') }), count > 0 && _jsx("p", { className: "subtitle", "data-translate": "index_active_jobs_subtitle", children: subtitleStr })] }) }));
}
/**
 * A fix pozíciójú karrier-fejléc aktuálisan látható magassága (px), 0 ha nincs vagy rejtett.
 * [CU-869eth2x5] `position: fixed` elemnél az offsetParent null, ezért a bounding rect a mérce.
 */
function getStickyHeaderHeight() {
    const headerEl = document.querySelector('[data-career-header="true"]');
    if (!headerEl)
        return 0;
    const style = window.getComputedStyle(headerEl);
    if (style.display === 'none' || style.visibility === 'hidden')
        return 0;
    const rect = headerEl.getBoundingClientRect();
    // Csak akkor takar, ha a viewport tetején ül (fixed/sticky) — egyébként a flow-ban van, nem fed.
    if (style.position !== 'fixed' && style.position !== 'sticky')
        return 0;
    return rect.height > 0 ? Math.round(rect.height) : 0;
}
export function IndexView({ config }) {
    useTracking({ module: 'index' });
    const { t } = useT();
    const { result, loading, search } = useSearch();
    const [hasSearched, setHasSearched] = useState(false);
    // Highlight kategória search-state — a CategoryListWithHighlights wrapper trigger-ei mozgatjak
    const [highlightFilter, setHighlightFilter] = useState({ main: 0, sub: 0 });
    // is_jobs_scroll_to_search: toltodes utan automatikusan scroll a talalatok blokkjahoz (regi karrier oldal flag)
    const jobsBlockRef = React.useRef(null);
    const shouldScroll = !!config.ui_config?.is_jobs_scroll_to_search;
    /**
     * Görgetés a találati blokkhoz (sticky Header magasságával korrigálva).
     *  - 'list': a tényleges találati listára (`.career-jobs-list`) — a betöltés utáni
     *    auto-scroll (is_jobs_scroll_to_search flag) régi viselkedése;
     *  - 'block': a jobs-blokk tetejére, ahol az "X találat" számláló sor van —
     *    [CU-869eth2x5] a Keresés gomb megnyomása után ide görgetünk, hogy a
     *    találat-szám és a lista eleje egyszerre látszódjon (desktop + mobil).
     * Double requestAnimationFrame: a state-commit utáni layout stabilizáció —
     * egy frame nem mindig elég, mert a rerender közben futhat font/image load.
     */
    const scrollToJobsBlock = useCallback((target) => {
        requestAnimationFrame(() => {
            requestAnimationFrame(() => {
                if (!jobsBlockRef.current)
                    return;
                // A Header `position: fixed` (top-0), ezért az `offsetParent`-je MINDIG null — azzal
                // nem mérhető a láthatósága (ez okozta, hogy mobilon a fejléc kitakarta az
                // "X találat" sort). A látható magasságot a getBoundingClientRect adja; a
                // display:none-os (nem renderelt) fejléc magassága 0 → minimális offset (16px).
                const offset = getStickyHeaderHeight() + 16;
                // A jobsBlockRef a wrapper div-re mutat (Betöltés.. / Nincs találat / X találat felirat is benne van).
                // A tényleges TALÁLATOK a `.career-jobs-list` belső divben.
                const jobsListEl = target === 'list'
                    ? jobsBlockRef.current.querySelector('.career-jobs-list')
                    : null;
                const targetEl = jobsListEl || jobsBlockRef.current;
                const targetY = targetEl.getBoundingClientRect().top + window.scrollY - offset;
                try {
                    window.scrollTo({ top: targetY, behavior: 'smooth' });
                }
                catch (_) {
                    // Régi böngészők fallback: object-form options nem támogatott.
                    window.scrollTo(0, targetY);
                }
            });
        });
    }, []);
    useEffect(() => {
        // URL paraméterek alapján auto-search (megosztott link → ugyanaz a keresési state).
        // A useSearchUrlSync hook a régi Jobs/Index.php-kompatibilis paraméter neveket olvassa
        // (keyword, category, location, locationext, jobworktype, experience, client, maincategory).
        // [B-CAREERREGIO] ugyanaz a lokáció-mód szabály, mint a SearchBar beküldésénél: extended
        // módban az URL ?location= / ?city= nem él, különben a ?locationext= nem - így az induló
        // keresés és a mezők állapota nem térhet el.
        const urlParams = applyLocationMode(readUrlParams(), !!config.ui_config?.is_filters_location_extended);
        if (urlParams.mainCategory > 0) {
            setHighlightFilter({ main: urlParams.mainCategory, sub: urlParams.category });
        }
        const initParams = toBackendParams(urlParams, { offset: 0, limit: 20 });
        search(initParams).then(() => {
            setHasSearched(true);
            if (shouldScroll) {
                scrollToJobsBlock('list');
            }
        }).catch(() => { });
    }, [search, shouldScroll, scrollToJobsBlock]);
    const handleSearch = (params, source = 'submit', changedField) => {
        // SearchBar-ből jött params + a highlightFilter state-jét összekombináljuk
        const merged = { ...params };
        // [B-HLPILLNOFILTER] A kereső kategória-legördülőjének EXPLICIT módosítása (changedField ===
        // 'category') globális kategória-választás: a kiemelt szűrő (main + sub) törlődik (URL-ből is),
        // és csak a legördülő értéke szűr - a "Minden kategória" (0) így a kategória-szűrést teljesen
        // törli. Különben a kiemelt fő-kategória AND a legördülő kategóriája egy-kategóriás állásoknál
        // mindig 0 találatot adna (digic). Más mező (munkatípus, lokáció, kulcsszó beküldése) a kiemelt
        // szűrőt (main AND sub) érintetlenül hagyja.
        if (changedField === 'category' && (highlightFilter.main > 0 || highlightFilter.sub > 0)) {
            setHighlightFilter({ main: 0, sub: 0 });
            pushUrlParams({ mainCategory: 0 });
        }
        else {
            if (highlightFilter.main > 0) {
                merged.maincategory = highlightFilter.main;
                // [B-HLPILLNOFILTER] A legördülő csak-fő-kategóriás állapotban a fő-kategóriát MUTATJA
                // (SearchBar kezdőérték), de az nem al-kategória: a kérésből és az URL-ből kivesszük,
                // hogy ne legyen maincategory=X&category=X (azonos eredmény, tiszta állapot).
                if (highlightFilter.sub === 0 && merged.category === highlightFilter.main) {
                    delete merged.category;
                    pushUrlParams({ category: 0 });
                }
            }
            // Ha sub-cat is van, az felülírja a SearchBar `category`-jét — vagy a kettő AND-szűrést kap a backend
            if (highlightFilter.sub > 0) {
                merged.category = highlightFilter.sub;
            }
        }
        // [CU-869eth2x5] KIZÁRÓLAG a Keresés gomb (source='submit') után görgetünk a találati
        // blokkhoz ("X találat") — a keresőmező alatt a lista sokszor a hajtás alá esik (főleg
        // mobilon). A select-mezők változása (source='change') csak szűr, nem görget.
        search(merged).then(() => {
            setHasSearched(true);
            if (source === 'submit') {
                scrollToJobsBlock('block');
            }
        }).catch(() => { });
    };
    // CategoryListWithHighlights callback — main + sub valtozas triggerelje a search-et + URL-be irast
    // [B-HLPILLNOFILTER] (academy #2260) a kiemelt pill kattintasa is ide jon (mainId, 0), a Vissza
    // gomb (0, 0). A keres parameterei a frissitett URL-bol epulnek (ugyanaz a minta, mint az indulo
    // kereses): igy a SearchBar altal az URL-be irt kulcsszo / munkatipus / varos szuro megmarad,
    // es (0, 0)-nal a maincategory/category egyszeruen kimarad -> teljes lista a tobbi szurovel.
    const handleHighlightSelect = (mainId, subId) => {
        const sub = subId || 0;
        setHighlightFilter({ main: mainId, sub });
        pushUrlParams({ mainCategory: mainId, category: sub });
        const urlParams = applyLocationMode(readUrlParams(), !!config.ui_config?.is_filters_location_extended);
        search(toBackendParams(urlParams, { offset: 0, limit: 20 })).catch(() => { });
    };
    // CategoryList callback — onSelect → URL-be írás + search a kiválasztott kategóriával
    const handleCategorySelect = (catId) => {
        setHighlightFilter({ main: 0, sub: 0 });
        pushUrlParams({ category: catId, mainCategory: 0 });
        search({
            category: catId,
            offset: 0,
            limit: 20,
        }).catch(() => { });
    };
    const jobs = result?.jobs || [];
    const count = result?.count || 0;
    const isFeatured = !!config.ui_config?.is_featured_jobs;
    // Derived: a kiemelt kategoria mod akkor aktiv, ha az admin toggle on ES van legalabb 1 highlight.
    // A nev kifejezett, igy a komponens API-k nem hivatkoznak a feature-flag is_featured_jobs nevre.
    const showHighlightCategories = isFeatured && Array.isArray(config.highlight_categories) && config.highlight_categories.length > 0;
    const catDisplayMode = (config.ui_config?.categories_display_mode || 'text_image');
    // Hero elrendezés beállítások (admin Beállítások/Karrieroldal/Hero elrendezés).
    // overlay: a kereső a hero-BAN jelenik meg (régi karrieroldal paritás), a main-ben
    // a search modul helyén csak a Banner marad.
    const heroLayout = config.ui_config?.hero_layout === 'overlay' ? 'overlay' : 'classic';
    const heroHeight = Number(config.ui_config?.hero_height || 0);
    const heroAspect = config.ui_config?.hero_aspect || '';
    const heroTextAlign = config.ui_config?.hero_text_align === 'left' ? 'left' : 'center';
    const showPageHeading = config.ui_config?.is_page_heading !== false;
    const disabledSectionsCfg = config.ui_config?.index_disable_sections || [];
    const searchModuleActive = config.modules_order.some(m => m.type === 'search')
        && disabledSectionsCfg.indexOf('search') === -1;
    const searchInHero = heroLayout === 'overlay' && searchModuleActive;
    // Lista-címsor (legacy: controllers.actual_jobs → "Aktuális állások"). Üres = nem jelenik meg.
    const jobsListTitle = (t('jobs_list_title', '') || '').trim();
    // [B-HLPILLNOFILTER] A SearchBar a mezőit az URL-ből CSAK mountkor olvassa; a kiemelt pill /
    // Vissza / al-pill az URL category-ját átírja, ezért a kulcs a kiemelt szűrővel együtt vált
    // (újramount = a legördülők a friss URL-állapotot mutatják, nem egy korábbi választást).
    const searchBarEl = (_jsx(SearchBar, { categories: config.categories, locations: config.locations, jobWorkTypes: config.job_work_types, experienceLevels: config.experience_levels, experienceOtherLevels: config.experience_other_levels || [], clients: config.clients, filtersOrder: config.filters_order, uiConfig: config.ui_config, onSearch: handleSearch, variant: searchInHero ? 'inline' : 'card' }, `searchbar-${highlightFilter.main}-${highlightFilter.sub}`));
    return (_jsxs(_Fragment, { children: [_jsx(Header, { config: config }), _jsx(Hero, { title: config.content.title, subtitle: config.content.subtitle, images: config.header.images, videoUrl: config.header.video_url, jobsCount: config.jobs_count, heightPx: heroHeight, aspect: heroAspect, textAlign: heroTextAlign, children: searchInHero ? searchBarEl : undefined }), showPageHeading && _jsx(PageHeading, { count: config.jobs_count }), _jsxs("main", { className: "container mx-auto px-4 py-8", children: [config.modules_order.map((mod, i) => {
                        if (mod.type === 'search') {
                            if ((config.ui_config?.index_disable_sections || []).indexOf('search') !== -1)
                                return null;
                            // Banner (admin Beállítások / Karrieroldal / Banner) közvetlenül a keresősáv UTÁN,
                            // a következő modul (kategóriák / jobs) ELŐTT jelenik meg. Csak akkor renderelődik,
                            // ha az admin 'Látható' checkbox ON ÉS van banner kép feltöltve (a Banner komponens
                            // szigorúan ellenőrzi a visible+image feltételt — különben null-t ad vissza).
                            // Overlay hero elrendezésnél a SearchBar a Hero-ban renderelődik, itt csak a
                            // Banner marad a search modul pozíciójában.
                            return (_jsxs(React.Fragment, { children: [!searchInHero && searchBarEl, _jsx(Banner, { banner: config.header.banner })] }, `mod-${i}-search`));
                        }
                        // index_disable_sections: regi karrier oldal flag — egyes szekciok teljes elrejtese
                        const disabledSections = config.ui_config?.index_disable_sections || [];
                        if (mod.type === 'categories') {
                            if (disabledSections.indexOf('category') !== -1)
                                return null;
                            // Ha sem normal categoriak sem highlight nincs, ne reneljunk semmit
                            if (config.categories.length === 0 && !showHighlightCategories)
                                return null;
                            return (_jsx(CategoryListWithHighlights, { categories: config.categories, highlightCategories: config.highlight_categories || [], enabled: showHighlightCategories, mode: catDisplayMode, onCategorySelect: handleCategorySelect, onHighlightFilter: handleHighlightSelect, selectedMainId: highlightFilter.main }, `mod-${i}-cat`));
                        }
                        if (mod.type === 'jobs') {
                            if (disabledSections.indexOf('jobs') !== -1)
                                return null;
                            return (_jsxs("div", { ref: jobsBlockRef, children: [jobsListTitle !== '' && (_jsx("h2", { className: "career-jobs-heading", "data-translate": "jobs_list_title", children: jobsListTitle })), loading && _jsx("p", { className: "text-center py-8", "data-translate": "loading", children: t('loading', 'Betöltés...') }), !loading && hasSearched && jobs.length === 0 && (_jsx("p", { className: "text-center py-8", "data-translate": "no_results", children: t('no_results', 'Nincs találat') })), jobs.length > 0 && (_jsxs(_Fragment, { children: [_jsxs("p", { className: "text-center py-4", children: [count, " ", _jsx("span", { "data-translate": "jobs_found", children: t('jobs_found', 'aktív állás') })] }), _jsx(JobList, { jobs: jobs, config: config })] }))] }, `mod-${i}-jobs`));
                        }
                        // Sablon-modul (sablon_id > 0): a getSiteConfig beágyazza az admin sablon HTML-jét
                        // (mod.html, kontakt-nyelv szerint) — a legacy createTemplateOrder getSablon() párja.
                        // Azonos bizalmi szint, mint a ContentView (admin-szerkesztett sablon tartalom).
                        if (mod.type === 'sablon' && mod.sablon_id > 0) {
                            const sablonHtml = (mod.html || '').trim();
                            if (sablonHtml === '')
                                return null;
                            return (_jsx("section", { className: "career-sablon-module", "data-sablon-id": mod.sablon_id, dangerouslySetInnerHTML: { __html: sablonHtml } }, `mod-${i}-sablon-${mod.sablon_id}`));
                        }
                        return null;
                    }), config.cta.visible && _jsx(Cta, { cta: config.cta })] }), _jsx(Footer, { config: config })] }));
}
